import React, { useState, useEffect, useRef } from 'react';
import { Plus, Trash2, X, Upload, ClipboardPaste } from 'lucide-react';
import { Button } from "../../../components/ui/Button";
import { DEFAULT_QUOTATION_COLUMNS, DEFAULT_QUOTATION_ROW } from '../constants/quotation_constants';
import { API_BASE_URL as BASE_URL } from '../../../services/api';

const resolveImageUrl = (img) => {
  if (!img) return '';
  if (img.startsWith('http') || img.startsWith('data:')) return img;
  return `${BASE_URL}${img.startsWith('/') ? '' : '/'}${img}`;
};

export const InitialQuotationTable = ({ onTotalChange, onDataChange, stockSelections = {}, tableData = null }) => {
  const [columns, setColumns] = useState({ ...DEFAULT_QUOTATION_COLUMNS });
  const [rows, setRows] = useState(() => {
    if (tableData?.rows && Array.isArray(tableData.rows) && tableData.rows.length > 0) {
      return tableData.rows;
    }
    return [{ id: 1, ...DEFAULT_QUOTATION_ROW, amount: 0, isStock: false, isLocked: false, _stockKey: null }];
  });

  const [discountType, setDiscountType] = useState(() => tableData?.discountType || 'percentage');
  const [discountValue, setDiscountValue] = useState(() => tableData?.discountValue || 0);

  const [gstType, setGstType] = useState(() => tableData?.gstType || 'percentage');
  const [gstValue, setGstValue] = useState(() => tableData?.gstValue || 0);

  const onTotalChangeRef = useRef(onTotalChange);
  const onDataChangeRef = useRef(onDataChange);

  useEffect(() => {
    onTotalChangeRef.current = onTotalChange;
    onDataChangeRef.current = onDataChange;
  });

  const prevTableRowsRef = useRef(tableData?.rows || null);
  useEffect(() => {
    if (tableData?.rows && Array.isArray(tableData.rows) && tableData.rows !== prevTableRowsRef.current) {
      prevTableRowsRef.current = tableData.rows;
      if (JSON.stringify(tableData.rows) !== JSON.stringify(rows)) {
        if (tableData.rows.length === 0) {
          setRows([{ id: Date.now(), ...DEFAULT_QUOTATION_ROW, amount: 0, isStock: false, isLocked: false, _stockKey: null }]);
        } else {
          setRows(tableData.rows);
        }
      }
    }
    if (tableData?.discountType && tableData.discountType !== discountType) {
      setDiscountType(tableData.discountType);
    }
    if (tableData?.discountValue !== undefined && tableData.discountValue !== discountValue) {
      setDiscountValue(tableData.discountValue);
    }
    if (tableData?.gstType && tableData.gstType !== gstType) {
      setGstType(tableData.gstType);
    }
    if (tableData?.gstValue !== undefined && tableData.gstValue !== gstValue) {
      setGstValue(tableData.gstValue);
    }
  }, [tableData]);

  const prevStockRef = useRef({});
  useEffect(() => {
    const currentKeys = Object.keys(stockSelections);
    const prevKeys = Object.keys(prevStockRef.current);

    const added = currentKeys.filter(k => !prevKeys.includes(k));
    const removed = prevKeys.filter(k => !currentKeys.includes(k));

    if (added.length > 0 || removed.length > 0) {
      setRows(currentRows => {
        let newRows = [...currentRows];
        // Remove deselected
        removed.forEach(key => {
          newRows = newRows.filter(r => r._stockKey !== key);
        });
        // Add newly selected
        added.forEach(key => {
          const item = stockSelections[key];
          newRows.push({
            id: Date.now() + Math.random(),
            type: 'item',
            particulars: item.flatItem.itemName,
            vendor: item.flatItem.vendor || '',
            description: `Vendor: ${item.flatItem.vendor}\nInvoice: ${item.flatItem.invoiceNo}`,
            quantity: item.qty,
            amount: item.flatItem.purchaseRate || 0,
            _stockKey: key,
            isStock: true,
            isLocked: true, // Lock everything except image
            _fromStock: true,
            source: 'store', // Force source=store so backend knows it's stock
            itemId: item.flatItem.itemId,
            storeId: item.flatItem.storeId
          });
        });
        return newRows;
      });
    }

    // Handle quantity updates for existing stock selections
    currentKeys.forEach(key => {
       if (prevKeys.includes(key)) {
          if (stockSelections[key].qty !== prevStockRef.current[key].qty) {
             setRows(currentRows => currentRows.map(r => r._stockKey === key ? { ...r, quantity: stockSelections[key].qty } : r));
          }
       }
    });

    prevStockRef.current = stockSelections;
  }, [stockSelections]);

  useEffect(() => {
    const subTotal = rows.reduce((sum, row) => sum + ((Number(row.quantity) || 1) * (Number(row.amount) || 0)), 0);
    const discountAmount = discountType === 'percentage'
      ? (subTotal * (Number(discountValue) || 0) / 100)
      : (Number(discountValue) || 0);
    const taxableAmount = Math.max(0, subTotal - discountAmount);

    const gstAmount = gstType === 'percentage'
      ? (taxableAmount * (Number(gstValue) || 0) / 100)
      : (Number(gstValue) || 0);

    const grandTotal = taxableAmount + gstAmount;

    if (onTotalChangeRef.current) {
      onTotalChangeRef.current(grandTotal);
    }
    if (onDataChangeRef.current) {
      onDataChangeRef.current({
        rows,
        subTotal,
        discountAmount,
        gstAmount,
        grandTotal,
        discountType,
        discountValue,
        gstType,
        gstValue
      });
    }
  }, [rows, discountType, discountValue, gstType, gstValue]);

  const addRow = () => {
    setRows([
      ...rows,
      { id: Date.now(), type: 'item', ...DEFAULT_QUOTATION_ROW, amount: 0, isStock: false, isLocked: false, _stockKey: null }
    ]);
  };

  const addHorizontalRow = () => {
    setRows([
      ...rows,
      { id: Date.now(), type: 'horizontal', particulars: '' }
    ]);
  };

  const removeRow = (id) => {
    setRows(rows.filter(row => row.id !== id));
  };

  const handleRowChange = (id, field, value) => {
    setRows(rows.map(row => {
      if (row.id === id) {
        return { ...row, [field]: value };
      }
      return row;
    }));
  };

  const handleColumnChange = (field, value) => {
    setColumns(prev => ({ ...prev, [field]: value }));
  };

  const subTotal = rows.reduce((sum, row) => sum + ((Number(row.quantity) || 1) * (Number(row.amount) || 0)), 0);
  const discountAmount = discountType === 'percentage'
    ? (subTotal * (Number(discountValue) || 0) / 100)
    : (Number(discountValue) || 0);
  const taxableAmount = Math.max(0, subTotal - discountAmount);

  const gstAmount = gstType === 'percentage'
    ? (taxableAmount * (Number(gstValue) || 0) / 100)
    : (Number(gstValue) || 0);

  const grandTotal = taxableAmount + gstAmount;

  // Stock rows always appear first, manual rows after
  const sortedRows = [
    ...rows.filter(r => r._fromStock || r.isLocked),
    ...rows.filter(r => !r._fromStock && !r.isLocked)
  ];

  return (
    <div className="flex flex-col gap-2 w-full">
      <div className="flex justify-end items-center mb-1">
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Plus size={14} />}
            onClick={addHorizontalRow}
            className="text-slate-600 border-slate-300 hover:bg-slate-50 h-8 text-xs"
          >
            Row
          </Button>
          <Button
            variant="outline"
            size="sm"
            leftIcon={<Plus size={14} />}
            onClick={addRow}
            className="text-brand-blue border-brand-blue hover:bg-blue-50 h-8 text-xs"
          >
            Item
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-md border border-slate-200">
        <table className="w-full text-sm text-left text-slate-600 min-w-[850px]">
          <thead className="text-[11px] text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
            <tr>
              <th scope="col" className="px-2 py-2 w-[4%] text-center" title="Stock Item (not in PDF)">
                <span className="text-[10px] font-semibold text-slate-400">STK</span>
              </th>
              <th scope="col" className="px-2 py-2 w-[21%]">
                <input
                  type="text"
                  value={columns.col1}
                  onChange={(e) => handleColumnChange('col1', e.target.value)}
                  className="w-full bg-transparent border-none outline-none font-medium text-slate-500 uppercase p-0 focus:ring-0"
                  placeholder="Particulars"
                />
              </th>
              <th scope="col" className="px-2 py-2 w-[12%]">
                <input
                  type="text"
                  value={columns.col2}
                  onChange={(e) => handleColumnChange('col2', e.target.value)}
                  className="w-full bg-transparent border-none outline-none font-medium text-slate-500 uppercase p-0 focus:ring-0"
                  placeholder="Image"
                />
              </th>
              <th scope="col" className="px-2 py-2 w-[12%]">
                <input
                  type="text"
                  value={columns.colVendor || 'Vendor'}
                  onChange={(e) => handleColumnChange('colVendor', e.target.value)}
                  className="w-full bg-transparent border-none outline-none font-medium text-slate-500 uppercase p-0 focus:ring-0"
                  placeholder="Vendor"
                />
              </th>
              <th scope="col" className="px-2 py-2 w-[28%]">
                <input
                  type="text"
                  value={columns.col3}
                  onChange={(e) => handleColumnChange('col3', e.target.value)}
                  className="w-full bg-transparent border-none outline-none font-medium text-slate-500 uppercase p-0 focus:ring-0"
                  placeholder="Description"
                />
              </th>
              <th scope="col" className="px-2 py-2 w-[10%]">
                <input
                  type="text"
                  value={columns.col4}
                  onChange={(e) => handleColumnChange('col4', e.target.value)}
                  className="w-full bg-transparent border-none outline-none font-medium text-slate-500 uppercase p-0 focus:ring-0 text-center"
                  placeholder="Quantity"
                />
              </th>
              <th scope="col" className="px-2 py-2 w-[10%] text-right">
                <input
                  type="text"
                  value={columns.col5 || 'Amount'}
                  onChange={(e) => handleColumnChange('col5', e.target.value)}
                  className="w-full bg-transparent border-none outline-none font-medium text-slate-500 uppercase p-0 focus:ring-0 text-right"
                  placeholder="Amount"
                />
              </th>
              <th scope="col" className="px-2 py-2 w-[5%] text-center"></th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => (
              <tr key={row.id} className={`bg-white border-b border-slate-100 hover:bg-slate-50 transition-colors ${(row._fromStock || row.isStock || row.source === 'store') ? 'bg-blue-50/30' : ''} ${row.type === 'horizontal' ? 'bg-slate-50 font-medium' : ''}`}>
                {row.type === 'horizontal' ? (
                  <>
                    <td className="px-2 py-1 text-center align-middle">
                      {/* no stock indicator for section rows */}
                    </td>
                    <td className="px-2 py-1" colSpan={6}>
                      <input
                        type="text"
                        value={row.particulars}
                        onChange={(e) => handleRowChange(row.id, 'particulars', e.target.value)}
                        className="w-full px-1 py-1 text-sm bg-transparent border border-transparent hover:border-slate-300 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue rounded outline-none transition-all font-semibold text-slate-800 text-center"
                        placeholder="Enter section heading or note..."
                      />
                    </td>
                    <td className="px-2 py-1 text-center">
                      <button
                        onClick={() => removeRow(row.id)}
                        className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                        title="Remove row"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    {/* Stock indicator — first column, read-only */}
                    <td className="px-2 py-2 align-middle text-center">
                      {(row._fromStock || row.isStock || row.source === 'store') ? (
                        <div className="flex flex-col items-center gap-1">
                          <div className="w-4 h-4 rounded bg-brand-blue flex items-center justify-center" title="Stock Item — excluded from PDF">
                            <svg width="9" height="7" viewBox="0 0 10 8" fill="none">
                              <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                          <span className="text-[8px] font-bold text-brand-blue uppercase">Stock</span>
                        </div>
                      ) : (
                        <div className="w-4 h-4 rounded border-2 border-slate-200 bg-white" title="Not a stock item" />
                      )}
                    </td>
                    <td className="px-1 py-1 align-top">
                      {(row._fromStock || row.isStock || row.source === 'store') ? (
                        <div className="w-full px-2 py-1 text-[13px] text-slate-700 font-medium leading-relaxed min-h-[48px] bg-blue-50/50 rounded border border-blue-100">
                          {row.particulars}
                        </div>
                      ) : (
                        <textarea
                          rows={2}
                          value={row.particulars}
                          onChange={(e) => handleRowChange(row.id, 'particulars', e.target.value)}
                          className="w-full px-2 py-1 text-[13px] bg-transparent border border-transparent hover:border-slate-300 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue rounded outline-none transition-all resize-y min-h-[48px] leading-relaxed"
                          placeholder="Enter particulars... (Use Shift+Enter for multiple lines)"
                        />
                      )}
                    </td>
                    <td className="px-1 py-1 align-top pt-2">
                      <div
                        className="relative border-2 border-dashed border-slate-200 rounded p-1 hover:border-brand-blue/50 transition-colors bg-slate-50/50 outline-none focus:ring-1 focus:ring-brand-blue min-h-[48px] flex items-center justify-center"
                        tabIndex={0}
                        onPaste={(e) => {
                          const items = e.clipboardData?.items;
                          if (!items) return;
                          for (let i = 0; i < items.length; i++) {
                            if (items[i].type.indexOf('image') !== -1) {
                              const file = items[i].getAsFile();
                              if (file) {
                                const reader = new FileReader();
                                reader.onloadend = () => {
                                  handleRowChange(row.id, 'image', reader.result);
                                };
                                reader.readAsDataURL(file);
                              }
                              break;
                            }
                          }
                        }}
                      >
                        {!row.image ? (
                          <div className="flex justify-center gap-2 w-full px-1">
                            <label
                              className="flex items-center justify-center p-2 bg-slate-200 text-slate-700 rounded cursor-pointer hover:bg-slate-300 transition-colors shadow-sm"
                              title="Upload Image"
                            >
                              <Upload size={14} />
                              <input
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) => {
                                  const file = e.target.files[0];
                                  if (file) {
                                    const reader = new FileReader();
                                    reader.onloadend = () => {
                                      handleRowChange(row.id, 'image', reader.result);
                                    };
                                    reader.readAsDataURL(file);
                                  }
                                }}
                              />
                            </label>
                            <button
                              type="button"
                              title="Paste Image from Clipboard"
                              onClick={async (e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                try {
                                  const clipboardItems = await navigator.clipboard.read();
                                  for (const clipboardItem of clipboardItems) {
                                    for (const type of clipboardItem.types) {
                                      if (type.startsWith('image/')) {
                                        const blob = await clipboardItem.getType(type);
                                        const reader = new FileReader();
                                        reader.onloadend = () => {
                                          handleRowChange(row.id, 'image', reader.result);
                                        };
                                        reader.readAsDataURL(blob);
                                        return;
                                      }
                                    }
                                  }
                                  alert('No image found in clipboard');
                                } catch (err) {
                                  console.error('Failed to read clipboard contents: ', err);
                                  alert('Could not paste image. Please check clipboard permissions or click inside the dashed box and press Ctrl+V.');
                                }
                              }}
                              className="flex items-center justify-center p-2 bg-slate-200 text-slate-700 rounded hover:bg-slate-300 transition-colors shadow-sm"
                            >
                              <ClipboardPaste size={14} />
                            </button>
                          </div>
                        ) : (
                          <div className="relative group flex justify-center w-full">
                            <img src={resolveImageUrl(row.image)} alt="preview" className="h-16 object-contain rounded" />
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowChange(row.id, 'image', '');
                              }}
                              className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-red-600"
                              title="Remove image"
                            >
                              <X size={12} />
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-2 py-2 align-top pt-3">
                      <input
                        type="text"
                        value={row.vendor || ''}
                        onChange={(e) => handleRowChange(row.id, 'vendor', e.target.value)}
                        disabled={row.isLocked}
                        className={`w-full px-2 py-1.5 text-[13px] rounded outline-none transition-all ${
                          row.isLocked
                            ? 'bg-slate-100 text-slate-500 cursor-not-allowed border-transparent select-none'
                            : 'bg-transparent border border-transparent hover:border-slate-300 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue'
                        }`}
                        placeholder="Vendor name"
                      />
                    </td>
                    <td className="px-2 py-2 align-top">
                      <textarea
                        rows={3}
                        value={row.description}
                        onChange={(e) => handleRowChange(row.id, 'description', e.target.value)}
                        disabled={row.isLocked}
                        className={`w-full px-2 py-1.5 text-[12px] bg-transparent border border-transparent rounded outline-none transition-all resize-y min-h-[70px] leading-relaxed ${row.isLocked ? 'text-slate-500 cursor-not-allowed' : 'hover:border-slate-300 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue'}`}
                        placeholder="Description.."
                      />
                    </td>
                    <td className="px-2 py-2 align-top">
                      <div className="flex justify-center mt-3">
                        <input
                          type="number"
                          value={row.quantity || ''}
                          onChange={(e) => handleRowChange(row.id, 'quantity', e.target.value)}
                          disabled={row.isLocked}
                          className={`w-16 px-2 py-1.5 text-center text-sm font-semibold bg-transparent border rounded outline-none transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${row.isLocked ? 'border-slate-200 text-slate-500 bg-slate-50 cursor-not-allowed' : 'border-slate-200 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue'}`}
                          placeholder="0"
                          min="0"
                        />
                      </div>
                    </td>
                    <td className="px-2 py-2 align-top text-right pt-4">
                      <input
                        type="number"
                        value={row.amount || ''}
                        onChange={(e) => handleRowChange(row.id, 'amount', e.target.value)}
                        disabled={row.isLocked}
                        className={`w-full px-2 py-1.5 text-right text-[13px] font-semibold bg-transparent border border-transparent rounded outline-none transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${row.isLocked ? 'text-slate-500 cursor-not-allowed' : 'hover:border-slate-300 focus:border-brand-blue focus:ring-1 focus:ring-brand-blue'}`}
                        placeholder="0.00"
                        min="0"
                      />
                    </td>
                    <td className="px-2 py-2 text-center align-top pt-3">
                      <button
                        onClick={() => removeRow(row.id)}
                        className={`p-1 rounded transition-colors ${row.isLocked ? 'text-slate-300 cursor-not-allowed' : 'text-slate-400 hover:text-red-500 hover:bg-red-50'}`}
                        disabled={row.isLocked}
                        title={row.isLocked ? "Cannot remove stock item from here (unselect it from the left)" : "Remove item"}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-slate-50 border-t border-slate-200">
            <tr>
              <td colSpan="5" className="px-2 py-2 text-right text-xs font-semibold text-slate-500">SUBTOTAL</td>
              <td className="px-2 py-2 text-right text-sm font-semibold text-slate-700">
                ₹{subTotal.toLocaleString('en-IN')}
              </td>
              <td></td>
              <td></td>
            </tr>
            <tr>
              <td colSpan="5" className="px-2 py-2 text-right text-xs font-semibold text-slate-500">
                <div className="flex items-center justify-end gap-2">
                  <span>DISCOUNT</span>
                  <div className="flex items-center bg-white border border-slate-200 rounded text-[11px] overflow-hidden">
                    <button
                      className={`px-2 py-1 ${discountType === 'percentage' ? 'bg-slate-100 font-bold text-brand-blue' : 'text-slate-500 hover:bg-slate-50'}`}
                      onClick={() => setDiscountType('percentage')}
                    >%</button>
                    <button
                      className={`px-2 py-1 border-l border-slate-200 ${discountType === 'fixed' ? 'bg-slate-100 font-bold text-brand-blue' : 'text-slate-500 hover:bg-slate-50'}`}
                      onClick={() => setDiscountType('fixed')}
                    >₹</button>
                  </div>
                  <input
                    type="number"
                    min="0"
                    value={discountValue}
                    onChange={(e) => setDiscountValue(e.target.value)}
                    className="w-16 px-1 py-1 text-[12px] bg-white border border-slate-200 rounded outline-none text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder="0"
                  />
                </div>
              </td>
              <td className="px-2 py-2 text-right text-sm font-semibold text-red-500">
                - ₹{discountAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
              <td></td>
            </tr>
            <tr className="bg-slate-50/50">
              <td colSpan="5" className="px-2 py-2 text-right text-xs font-semibold text-slate-500">TAXABLE AMOUNT</td>
              <td className="px-2 py-2 text-right text-sm font-semibold text-slate-700">
                ₹{taxableAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
              <td></td>
            </tr>
            <tr>
              <td colSpan="5" className="px-2 py-2 text-right text-xs font-semibold text-slate-500">
                <div className="flex items-center justify-end gap-2">
                  <span>GST</span>
                  <div className="flex items-center bg-white border border-slate-200 rounded text-[11px] overflow-hidden">
                    <button
                      className={`px-2 py-1 ${gstType === 'percentage' ? 'bg-slate-100 font-bold text-brand-blue' : 'text-slate-500 hover:bg-slate-50'}`}
                      onClick={() => setGstType('percentage')}
                    >%</button>
                    <button
                      className={`px-2 py-1 border-l border-slate-200 ${gstType === 'fixed' ? 'bg-slate-100 font-bold text-brand-blue' : 'text-slate-500 hover:bg-slate-50'}`}
                      onClick={() => setGstType('fixed')}
                    >₹</button>
                  </div>
                  <input
                    type="number"
                    min="0"
                    value={gstValue}
                    onChange={(e) => setGstValue(e.target.value)}
                    className="w-16 px-1 py-1 text-[12px] bg-white border border-slate-200 rounded outline-none text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    placeholder="0"
                  />
                </div>
              </td>
              <td className="px-2 py-2 text-right text-sm font-semibold text-slate-700">
                + ₹{gstAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
              <td></td>
            </tr>
            <tr className="border-t border-slate-200 bg-slate-100/50">
              <td colSpan="5" className="px-2 py-2 text-right text-xs font-bold text-slate-800">TOTAL</td>
              <td className="px-2 py-2 text-right text-sm font-bold text-brand-blue">
                ₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </td>
              <td></td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};
